import type { ApiResponse } from "@/features/auth/services/authApi";
import { apiClient } from "@/shared/services/api";

const unwrap = <T>(response: ApiResponse<T> | T): T =>
  response && typeof response === "object" && "data" in (response as ApiResponse<T>)
    ? (response as ApiResponse<T>).data
    : (response as T);

export interface ExpoPaymentApprovalBatch {
  id: string;
  batchNumber: string;
  expoId: string;
  expoName: string;
  paymentMethod: string;
  paymentChannel: "Cash" | "MobileMoney" | "Bank" | "Other" | string;
  status: string;
  reportedTotal: number;
  approvedTotal: number;
  varianceTotal: number;
  submittedByName: string;
  submittedAt?: string | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  notes?: string | null;
  rowVersion: number;
  items: {
    id: string;
    paymentId: string;
    paymentNumber: string;
    invoiceNumber?: string | null;
    orderNumber?: string | null;
    customerName: string;
    paymentReference?: string | null;
    reportedAmount: number;
    approvedAmount?: number | null;
    varianceAmount?: number | null;
    reviewStatus: string;
    paymentApprovalStatus: string;
  }[];
}

export interface ExpoCashHandover {
  id: string;
  handoverNumber: string;
  expoId: string;
  expoName: string;
  custodianName: string;
  status: string;
  expectedAmount: number;
  declaredAmount: number;
  receivedAmount?: number | null;
  varianceAmount?: number | null;
  declaredDifference?: number | null;
  handedOverByName?: string | null;
  handedOverAt?: string | null;
  receivedByName?: string | null;
  receivedAt?: string | null;
  reconciledByName?: string | null;
  reconciledAt?: string | null;
  notes?: string | null;
  rowVersion: number;
  items: {
    id: string;
    paymentId: string;
    paymentNumber: string;
    customerName: string;
    reportedAmount: number;
    approvedAmount?: number | null;
    varianceAmount?: number | null;
    reviewStatus: string;
    paymentApprovalStatus: string;
  }[];
}

export interface ExpoPaymentVariance {
  transactionType: string;
  transactionId: string;
  itemId?: string | null;
  expoId: string;
  expoName: string;
  transactionNumber: string;
  invoiceNumber?: string | null;
  paymentReference?: string | null;
  paymentMethod?: string | null;
  paymentChannel?: "Cash" | "MobileMoney" | "Bank" | "Other" | string | null;
  salespersonOrCustodianName?: string | null;
  reportedAmount: number;
  approvedOrReceivedAmount: number;
  varianceAmount: number;
  reason: string;
  status: string;
  ageDays: number;
  reviewerName?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  isResolved: boolean;
}

export const expoPaymentWorkflowsApi = {
  batches: async (expoId: string): Promise<ExpoPaymentApprovalBatch[]> => {
    const { data } = await apiClient.get<ApiResponse<ExpoPaymentApprovalBatch[]> | ExpoPaymentApprovalBatch[]>(
      "/expo-payment-workflows/batches",
      { params: { expoId } },
    );
    return unwrap(data) ?? [];
  },
  cashHandovers: async (expoId: string): Promise<ExpoCashHandover[]> => {
    const { data } = await apiClient.get<ApiResponse<ExpoCashHandover[]> | ExpoCashHandover[]>(
      "/expo-payment-workflows/cash-handovers",
      { params: { expoId } },
    );
    return unwrap(data) ?? [];
  },
  variances: async (expoId: string): Promise<ExpoPaymentVariance[]> => {
    const { data } = await apiClient.get<ApiResponse<ExpoPaymentVariance[]> | ExpoPaymentVariance[]>(
      "/expo-payment-workflows/variances",
      { params: { expoId } },
    );
    return unwrap(data) ?? [];
  },
};
